import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CourtLines, FullCourtLines } from '@/components/CourtCanvas';
import { Trash2, Undo2, X as XIcon, Maximize2, Minimize2 } from 'lucide-react';

/**
 * TraditionalBoard — "Pizarra de dibujo".
 *
 * Minimal full-screen drawing board for live coaching. No players, no
 * templates, no AI, no save/share. Three core controls (Media / Completa /
 * Borrar) plus quality-of-life: color picker, Undo, native fullscreen,
 * close.
 *
 * Critical touch behaviour: the SVG and the wrapping container both set
 * `touch-action: none` so a finger drag draws instead of pan-scrolling the
 * page. The page is `fixed inset-0 flex flex-col` so nothing under it scrolls.
 */
const V = 1000;

const COLORS = [
  { id: 'white', value: '#FFFFFF', label: 'Blanco' },
  { id: 'gold',  value: '#D4AF37', label: 'Oro'    },
  { id: 'red',   value: '#EF4444', label: 'Rojo'   },
  { id: 'cyan',  value: '#22D3EE', label: 'Cian'   },
  { id: 'green', value: '#10B981', label: 'Verde'  },
];

// Stroke thicknesses match the visual ramp coaches expect on a chalkboard:
// fino = highlight strokes, medio = default, grueso = bold annotations.
const WIDTHS = [
  { id: 'fino',    value: 3,  label: 'Fino'    },
  { id: 'medio',   value: 6,  label: 'Medio'   },
  { id: 'grueso',  value: 10, label: 'Grueso'  },
];

export default function TraditionalBoard() {
  const navigate = useNavigate();
  const svgRef = useRef(null);
  const rootRef = useRef(null);
  const [courtMode, setCourtMode] = useState('half'); // 'half' | 'full'
  // strokes: array of { pts: [{x,y}...], color: '#hex', width: number }
  const [strokes, setStrokes] = useState([]);
  const [current, setCurrent] = useState(null);
  const [color, setColor] = useState(COLORS[0].value);
  const [width, setWidth] = useState(WIDTHS[1].value); // 'medio' by default
  const [isFs, setIsFs] = useState(false);
  const drawingRef = useRef(false);

  // Track native fullscreen state so the toggle button icon stays in sync.
  useEffect(() => {
    const onFsChange = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const toSvg = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const m = svg.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    const p = pt.matrixTransform(m.inverse());
    return { x: p.x, y: p.y };
  }, []);

  const onPointerDown = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    try { svgRef.current?.setPointerCapture?.(e.pointerId); } catch { /* no-op */ }
    const p = toSvg(e);
    setCurrent({ pts: [p], color, width });
  };

  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = toSvg(e);
    setCurrent(prev => prev ? { ...prev, pts: [...prev.pts, p] } : { pts: [p], color, width });
  };

  const onPointerUp = (e) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    try { svgRef.current?.releasePointerCapture?.(e.pointerId); } catch { /* no-op */ }
    setCurrent(prev => {
      if (prev && prev.pts.length > 1) {
        setStrokes(s => [...s, prev]);
      }
      return null;
    });
  };

  const clearAll = () => {
    setStrokes([]);
    setCurrent(null);
  };

  const undo = () => {
    setStrokes(s => s.slice(0, -1));
  };

  const switchMode = (m) => {
    if (m === courtMode) return;
    setCourtMode(m);
    // Switching court layout invalidates strokes (different coordinate
    // space) — clearing them is intentional for this quick-scribble pad.
    setStrokes([]);
    setCurrent(null);
  };

  // Native fullscreen — falls back silently on iOS Safari (no support). Using
  // the rootRef element so even the toolbar enters fullscreen, not just the
  // SVG, keeping the controls reachable in immersive mode.
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await rootRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch { /* unsupported or denied — leave UI alone */ }
  };

  const pointsAttr = (pts) => pts.map(p => `${p.x},${p.y}`).join(' ');

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[80] bg-navy flex flex-col text-white"
      style={{ touchAction: 'none', overscrollBehavior: 'none' }}
      data-testid="traditional-board"
    >
      {/* Compact toolbar — single horizontal strip so the court gets all
          available vertical space on phones and tablets. */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gold/30 bg-navy-deep flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold/70 hidden sm:block">
            Pizarra
          </div>
          <div
            className="inline-flex rounded-full border-2 border-gold/40 overflow-hidden text-[11px] font-h uppercase tracking-wider"
            data-testid="board-court-toggle"
          >
            <button
              onClick={() => switchMode('half')}
              data-testid="board-court-half"
              className={`px-3 py-1.5 transition ${
                courtMode === 'half'
                  ? 'bg-gold text-navy font-semibold'
                  : 'text-gold/70 hover:text-gold hover:bg-gold/5'
              }`}
            >
              Media
            </button>
            <button
              onClick={() => switchMode('full')}
              data-testid="board-court-full"
              className={`px-3 py-1.5 transition ${
                courtMode === 'full'
                  ? 'bg-gold text-navy font-semibold'
                  : 'text-gold/70 hover:text-gold hover:bg-gold/5'
              }`}
            >
              Completa
            </button>
          </div>

          {/* Color picker — 5 swatches. Tapping changes only the color of
              future strokes; existing lines keep their colors. */}
          <div className="inline-flex items-center gap-1 px-1 rounded-full border border-gold/30 bg-navy" data-testid="board-color-picker">
            {COLORS.map(c => {
              const active = color === c.value;
              return (
                <button
                  key={c.id}
                  onClick={() => setColor(c.value)}
                  data-testid={`board-color-${c.id}`}
                  title={c.label}
                  aria-label={c.label}
                  className={`w-6 h-6 rounded-full transition ${
                    active ? 'ring-2 ring-white ring-offset-1 ring-offset-navy scale-110' : 'opacity-80 hover:opacity-100'
                  }`}
                  style={{ backgroundColor: c.value, border: '1px solid rgba(0,0,0,0.4)' }}
                />
              );
            })}
          </div>

          {/* Stroke width picker — three preset thicknesses. Visual dot scales
              with the actual stroke value so the choice is unambiguous. */}
          <div
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-gold/30 bg-navy"
            data-testid="board-width-picker"
          >
            {WIDTHS.map(w => {
              const active = width === w.value;
              const dot = Math.max(4, w.value);
              return (
                <button
                  key={w.id}
                  onClick={() => setWidth(w.value)}
                  data-testid={`board-width-${w.id}`}
                  title={w.label}
                  aria-label={w.label}
                  className={`w-6 h-6 inline-flex items-center justify-center rounded-full transition ${
                    active ? 'bg-gold/25 ring-2 ring-gold' : 'hover:bg-gold/10'
                  }`}
                >
                  <span
                    className="rounded-full bg-white block"
                    style={{ width: dot, height: dot }}
                  />
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={undo}
            disabled={strokes.length === 0}
            data-testid="board-undo"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gold/40 text-gold hover:bg-gold/10 text-[11px] font-h uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            title="Deshacer último trazo"
          >
            <Undo2 className="w-3.5 h-3.5" /> Deshacer
          </button>
          <button
            onClick={clearAll}
            data-testid="board-clear"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-red-400/50 text-red-300 hover:bg-red-500/10 text-[11px] font-h uppercase tracking-wider"
            title="Borrar todo"
          >
            <Trash2 className="w-3.5 h-3.5" /> Borrar
          </button>
          <button
            onClick={toggleFullscreen}
            data-testid="board-fullscreen"
            className="inline-flex items-center px-2.5 py-1.5 rounded-full border border-gold/40 text-gold hover:bg-gold/10"
            title={isFs ? 'Salir de pantalla completa' : 'Pantalla completa'}
            aria-label={isFs ? 'Salir de pantalla completa' : 'Pantalla completa'}
          >
            {isFs ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => navigate('/playbook')}
            data-testid="board-close"
            className="inline-flex items-center px-2.5 py-1.5 rounded-full border border-red-400/50 text-red-300 hover:bg-red-500/10"
            title="Salir"
            aria-label="Salir"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Drawing surface fills the rest of the viewport. */}
      <div className="flex-1 min-h-0 relative" style={{ touchAction: 'none' }}>
        <svg
          ref={svgRef}
          viewBox={courtMode === 'full' ? `0 0 ${V} ${2 * V}` : `0 0 ${V} ${V}`}
          preserveAspectRatio={courtMode === 'full' ? 'none' : 'xMidYMid meet'}
          className="w-full h-full block touch-none select-none"
          style={{
            touchAction: 'none',
            background: 'radial-gradient(ellipse at center top, #0E2440, #0A192F 70%)',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
          data-testid="board-svg"
        >
          {courtMode === 'full' ? <FullCourtLines /> : <CourtLines />}

          {strokes.map((s, i) => (
            <polyline
              key={i}
              points={pointsAttr(s.pts)}
              fill="none"
              stroke={s.color}
              strokeWidth={s.width}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.95"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {current && current.pts.length > 1 && (
            <polyline
              points={pointsAttr(current.pts)}
              fill="none"
              stroke={current.color}
              strokeWidth={current.width}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity="0.95"
              vectorEffect="non-scaling-stroke"
              data-testid="board-current-stroke"
            />
          )}
        </svg>
      </div>
    </div>
  );
}
